import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';
import { Popper } from '@m/alchemy-ui/Popper';
import { PopperBox } from '@m/alchemy-ui/PopperBox';
import { type CarouselSlideProps } from '../ChallengeCarousel/ChallengeCarouselSlide';
import { PlayPauseButton } from './PlayPauseButton';
import {
  CarouselContainer,
  CarouselLiveRegion,
  CarouselSlides,
  Dot,
  DotsContainer,
  NavButton
} from './Carousel.styled';

interface CarouselProps {
  slides: JSX.Element[];
  slidesLabels: string[];
  slidesAriaLabels: string[];
}

export const Carousel = ({ slides, slidesLabels, slidesAriaLabels }: CarouselProps) => {
  const intl = useIntl();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true); // play/pause button
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(false);
  const dotsContainerRef = React.useRef<HTMLDivElement>(null);
  const liveRegionRef = React.useRef<HTMLDivElement>(null);

  const isReallyPlaying = isPlaying && !isKeyboardFocused;
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);
  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides.length]);
  const getSlideAriaLabel = useCallback(
    (index: number) => {
      return slidesAriaLabels && slidesAriaLabels[index] ? slidesAriaLabels[index] : `Slide ${index + 1}`;
    },
    [slidesAriaLabels]
  );
  const getSlideLabel = useCallback(
    (index: number) => {
      return slidesLabels && slidesLabels[index] ? slidesLabels[index] : `Slide ${index + 1}`;
    },
    [slidesLabels]
  );

  useEffect(() => {
    if (isReallyPlaying) {
      const timer = setInterval(nextSlide, 5000);
      return () => clearInterval(timer);
    }
    return () => {};
  }, [isReallyPlaying, nextSlide]);

  const togglePlayPause = () => {
    if (isPlaying && isKeyboardFocused) {
      setIsPlaying(true);
    } else {
      setIsPlaying((prev) => !prev);
    }
  };

  const handleDotsKeyDown = (event: React.KeyboardEvent, currentIndex: number) => {
    let newIndex = currentIndex;
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = currentIndex === 0 ? slides.length - 1 : currentIndex - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = currentIndex === slides.length - 1 ? 0 : currentIndex + 1;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = slides.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        setCurrentSlide(currentIndex);

        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = `${intl.formatMessage({ id: 'selected', defaultMessage: 'Selected' })}. 
          ${intl.formatMessage({ id: 'carouselSlideUpdated', defaultMessage: 'Current slide updated' })}.`;
        }
        return;
      default:
    }

    const dots = dotsContainerRef.current?.querySelectorAll('[role="tab"]');
    if (dots && dots[newIndex]) {
      (dots[newIndex] as HTMLElement).focus();
    }
  };

  const handleFocusIn = () => {
    if (!isKeyboardFocused) {
      setIsKeyboardFocused(true);
    }
  };

  const handleFocusOut = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsKeyboardFocused(false);
    }
  };

  return (
    <>
      <CarouselContainer
        data-testid="carousel-container"
        id="carousel-container"
        className={isKeyboardFocused ? 'keyboard-focused' : ''}
        onFocus={handleFocusIn}
        onBlur={handleFocusOut}
      >
        <div
          role="group"
          data-testid="carousel-slides-container"
          aria-label={`${getSlideAriaLabel(currentSlide)}, ${currentSlide + 1} of ${slides.length}`}
        >
          <CarouselSlides data-testid="carousel-slides" currentSlide={currentSlide}>
            {slides.map((slide, index) =>
              React.isValidElement<Partial<CarouselSlideProps>>(slide) && typeof slide.type !== 'string'
                ? React.cloneElement(slide, {
                    tabIndex: index === currentSlide ? 0 : -1,
                    ...(index !== currentSlide && { inert: 'true' })
                  })
                : slide
            )}
          </CarouselSlides>
        </div>

        <NavButton
          aria-label={intl.formatMessage({ id: 'previousSlide', defaultMessage: 'Previous Slide' })}
          data-testid="carousel-left-nav-arrow"
          left
          onClick={prevSlide}
        >
          {String.fromCodePoint(10_094)}
        </NavButton>

        <NavButton
          aria-label={intl.formatMessage({ id: 'nextSlide', defaultMessage: 'Next Slide' })}
          data-testid="carousel-right-nav-arrow"
          onClick={nextSlide}
        >
          {String.fromCodePoint(10_095)}
        </NavButton>
      </CarouselContainer>

      <DotsContainer ref={dotsContainerRef} data-testid="carousel-navigation-container">
        <div
          role="tablist"
          aria-label={intl.formatMessage({ id: 'slideNavigation', defaultMessage: 'Slide Navigation' })}
          onFocus={handleFocusIn}
          onBlur={handleFocusOut}
        >
          {slides.map((_, index) => (
            <Popper
              key={_.key}
              placement="top"
              tooltipOptions={{ mode: 'name', delay: 50 }}
              mode="tooltip"
              overlay={
                <PopperBox maxWidth={200} hasArrow colorScheme="alchemyDark">
                  {getSlideLabel(index)}
                </PopperBox>
              }
              trigger={
                <Dot
                  key={_.key}
                  active={currentSlide === index}
                  onClick={() => setCurrentSlide(index)}
                  data-testid={`carousel-dot-${index}`}
                  role="tab"
                  tabIndex={currentSlide === index ? 0 : -1}
                  onKeyDown={(e) => handleDotsKeyDown(e, index)}
                  aria-selected={currentSlide === index}
                  aria-label={`${intl.formatMessage({ id: 'goToSlide', defaultMessage: 'Go to slide' })}: ${getSlideLabel(index)}.`}
                />
              }
            />
          ))}
        </div>

        <PlayPauseButton
          isPlaying={isPlaying}
          isAutoPaused={isKeyboardFocused}
          togglePlayPause={isKeyboardFocused ? () => setIsPlaying(true) : togglePlayPause}
          aria-label={
            isPlaying
              ? intl.formatMessage({ id: 'pauseCarousel', defaultMessage: 'Pause Carousel' })
              : intl.formatMessage({ id: 'playCarousel', defaultMessage: 'Play Carousel' })
          }
          aria-pressed={!isPlaying}
        />
      </DotsContainer>

      <CarouselLiveRegion ref={liveRegionRef} aria-live="polite" aria-atomic="true" />
    </>
  );
};
